import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { sendLiveRegionMessage } from '../../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { useFormContext } from '../../../../components/molecules/Form';
import { useFileUpload, FileUpload, getErrorFilesFromResponse } from '../../../FileUpload';
import FileInput from '../../../../components/molecules/Form/models/InputModels/FileInput';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Heading, HeadingHierarchy } from '../../Heading/Heading';
import PreventLostFocus from '../../../../components/atoms/PreventLostFocus/PreventLostFocus';
import { AttachedFilesPreviewList } from '../partials/AttachedFilesPreviewList';

type AttachedFilesBaseUploadFormTemplate = {
  id: string;
  fileSelectButtonLabel?: string;
  dropzoneText?: string;
  multiple?: true;
  headline: string;
  hierarchy: HeadingHierarchy;
};

export function AttachedFilesBaseUploadFormTemplate({
  id,
  fileSelectButtonLabel,
  dropzoneText,
  multiple,
  headline,
  hierarchy,
}: PropsWithChildren<AttachedFilesBaseUploadFormTemplate>) {
  const { t } = useSonTranslation('FileUpload');
  const [focusLastElement, setFocusLastElement] = useState<boolean>(false);
  const { status, inputs, onChange, hasChanges, model, response } = useFormContext();
  const { inputProps, labelProps, selection, removeFile, setValidity } = useFileUpload({
    id,
    input: { ...inputs.fileData, multiple } as unknown as FileInput,
    clearValue: true,
    onChange: files => {
      sendLiveRegionMessage(t('selectionChanged', { number: files.length }));
      onChange(
        inputs.fileData.name,
        files.map(({ file }) => file)
      );

      setFocusLastElement(false);
    },
  });
  const uploadButtonLabelRef = useRef(null);

  useEffect(() => {
    const validationErrors = selection.flatMap(({ errors }) => errors);
    if (inputs.fileData.constraints?.maxRequestSizeInBytes) {
      const { maxRequestSizeInBytes } = inputs.fileData.constraints;
      const requestSizeInBytes = selection.reduce((p, c) => p + c.file.size, 0);

      if (requestSizeInBytes > maxRequestSizeInBytes) {
        const maxSizeInMb = Math.round(maxRequestSizeInBytes / (1024 * 1024));
        validationErrors.push(t('validationErrorMessages.maxRequestSizeInBytes', { maxSize: maxSizeInMb }));
      }
    }

    setValidity(validationErrors);
  }, [inputs.fileData.constraints, selection, setValidity, t]);

  const errorFiles = getErrorFilesFromResponse(response);

  useEffect(() => {
    if (errorFiles.length) {
      model.reset();
    }
  }, [errorFiles.length, model]);

  const selectedFiles = inputs.fileData.value || [];

  return (
    <>
      <Heading hierarchy={hierarchy} className="attached-files-base__headline font-bold-15-19">
        {headline}
      </Heading>
      <PreventLostFocus attachTo={uploadButtonLabelRef}>
        <FileUpload
          labelTitle={fileSelectButtonLabel || t('fileSelectButtonLabel')}
          inputProps={inputProps}
          labelProps={labelProps}
          dropzoneText={dropzoneText}
          buttonLabelRef={uploadButtonLabelRef}
        >
          {selectedFiles.length > 0 && (
            <AttachedFilesPreviewList
              selection={selection.map(item => item.file)}
              hierarchy={hierarchy}
              buttonDisabled={
                status === InteractionStatus.SUCCESS || status === InteractionStatus.SENDING || !hasChanges()
              }
              focusLastElement={focusLastElement}
              onClick={file => {
                sendLiveRegionMessage(t('fileRemovedFromSelection', { fileName: file.name }));
                removeFile(file);
                setFocusLastElement(true);
              }}
            />
          )}
        </FileUpload>
      </PreventLostFocus>
    </>
  );
}
